import {ActionEvent, Controller} from '@hotwired/stimulus';
import {throttle} from 'throttle-debounce';
import {Typed} from 'app/stimulus/typed-stimulus';
import {AsStimulusController} from 'app/stimulus/as-stimulus-controller';
import {onLiveComponentRender} from 'app/stimulus/live-component-render';
import HorizontalOverflowScroller, {isOverflowSide} from 'app/utils/horizontal-overflow-scroller';
import InvalidArgumentException from 'app/errors/invalid-argument-exception';

const RESIZE_THROTTLE = 100;

/**
 * Turns the segments into a horizontal scroll strip once they do not fit: marks the group as overflowing
 * (side arrow columns, fade masks on the sides that can still scroll), pages by whole segments and keeps
 * the selected segment in view. The selection is rendered by the server (`is-selected` on a button
 * segment, a checked radio); Live re-applies the marker classes set here on top of every render, so the
 * state is measured again after each render.
 */
@AsStimulusController('segment-group')
export default class extends Typed(
    Controller<HTMLElement>, {
        targets: {
            item: HTMLElement,
            scrollContainer: HTMLElement,
            startControl: HTMLButtonElement,
            endControl: HTMLButtonElement,
        },
        classes: [
            'selected',
            'overflowing',
            'canScrollStart',
            'canScrollEnd',
        ] as const,
    }
)
{
    private readonly throttledResize = throttle(RESIZE_THROTTLE, (): void => {
        this.resize();
    });
    private overflowScroller: HorizontalOverflowScroller | null = null;
    private resizeObserver: ResizeObserver | null = null;
    private mutationObserver: MutationObserver | null = null;
    private removeRenderListener: (() => void) | null = null;
    private width: number | null = null;
    private widthChanged: boolean = false;
    private selectedItem: HTMLElement | null = null;
    private selectedItemVisible: boolean = true;

    public connect(): void {
        this.overflowScroller = new HorizontalOverflowScroller(
            this.scrollContainerTarget,
            (): HTMLElement[] => this.itemTargets,
            (): void => this.updateScrollState(),
        );
        this.overflowScroller.attach();
        // Showing the arrow columns changes only the strip, never the group width, so only a real width
        // change of the group is measured again.
        this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]): void => {
            const width = entries[entries.length - 1].contentRect.width;
            if (width === this.width) {
                return;
            }
            this.width = width;
            this.widthChanged = true;
            this.throttledResize();
        });
        this.resizeObserver.observe(this.element);
        this.mutationObserver = new MutationObserver((): void => {
            this.throttledResize();
        });
        this.mutationObserver.observe(this.scrollContainerTarget, {childList: true, subtree: true, characterData: true});
        this.removeRenderListener = onLiveComponentRender(this.element, (): void => {
            this.syncWithRender();
        });
        this.resize();
        this.selectedItem = this.findSelectedItem();
        this.scrollSelectedItemIntoView('auto');
        void document.fonts.ready.then((): void => {
            if (this.overflowScroller !== null) {
                this.throttledResize();
            }
        });
    }

    public disconnect(): void {
        // Cancels only the pending call; a full cancel would disable the throttle for a reconnect.
        this.throttledResize.cancel({upcomingOnly: true});
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        this.mutationObserver?.disconnect();
        this.mutationObserver = null;
        this.overflowScroller?.detach();
        this.overflowScroller = null;
        this.removeRenderListener?.();
        this.removeRenderListener = null;
        this.width = null;
        this.widthChanged = false;
        this.selectedItem = null;
        this.selectedItemVisible = true;
    }

    public page(event: ActionEvent): void {
        const side: unknown = event.params.side;
        if (!isOverflowSide(side)) {
            throw new InvalidArgumentException(`Unknown overflow side "${String(side)}".`);
        }
        void this.overflowScroller?.page(side);
    }

    /**
     * Keeps a segment selected without a re-render (a radio changed by the arrow keys) in view.
     */
    public revealSelectedItem(): void {
        this.selectedItem = this.findSelectedItem();
        this.scrollSelectedItemIntoView('smooth');
    }

    private resize(): void {
        if (this.overflowScroller === null) {
            return;
        }
        const wasOverflowing = this.isOverflowing();
        // A width change keeps the selected segment in view, but only if the user has not scrolled it away;
        // other measurements (segments or fonts changed) never move the strip of an overflowing group.
        const keepSelectedItemVisible = this.widthChanged && (!wasOverflowing || this.selectedItemVisible);
        this.widthChanged = false;
        // Measuring hides the arrow columns for a moment, which would reset the scroll position.
        const scrollLeft = this.scrollContainerTarget.scrollLeft;
        this.element.classList.remove(...this.overflowingClasses);
        if (this.overflowScroller.hasOverflow()) {
            this.element.classList.add(...this.overflowingClasses);
            this.scrollContainerTarget.scrollLeft = scrollLeft;
        }
        this.updateScrollState();
        if (!wasOverflowing || keepSelectedItemVisible) {
            this.scrollSelectedItemIntoView('auto');
        }
    }

    /**
     * A render of the surrounding Live component can change the segments or the selection; a newly
     * selected segment is scrolled into view, an unrelated render keeps the user's scroll position.
     */
    private syncWithRender(): void {
        this.resize();
        const selectedItem = this.findSelectedItem();
        if (selectedItem === this.selectedItem) {
            return;
        }
        this.selectedItem = selectedItem;
        this.scrollSelectedItemIntoView('smooth');
    }

    private updateScrollState(): void {
        const overflowing = this.isOverflowing();
        const canScrollStart = overflowing && this.overflowScroller !== null && this.overflowScroller.canScroll('start');
        const canScrollEnd = overflowing && this.overflowScroller !== null && this.overflowScroller.canScroll('end');
        this.element.classList.toggle(this.canScrollStartClass, canScrollStart);
        this.element.classList.toggle(this.canScrollEndClass, canScrollEnd);
        for (const control of this.startControlTargets) {
            control.disabled = !canScrollStart;
        }
        for (const control of this.endControlTargets) {
            control.disabled = !canScrollEnd;
        }
        this.selectedItemVisible = !overflowing || this.isSelectedItemVisible();
    }

    private scrollSelectedItemIntoView(behavior: ScrollBehavior): void {
        if (this.overflowScroller === null || !this.isOverflowing()) {
            return;
        }
        const item = this.findSelectedItem();
        if (item === null) {
            return;
        }
        this.selectedItemVisible = true;
        void this.overflowScroller.scrollItemIntoView(item, behavior);
    }

    private isSelectedItemVisible(): boolean {
        const item = this.findSelectedItem();
        return item !== null && this.overflowScroller !== null && this.overflowScroller.isItemVisible(item);
    }

    private findSelectedItem(): HTMLElement | null {
        // A radio segment carries the selection on its input instead of the selected class.
        return this.itemTargets.find((item: HTMLElement): boolean => {
            return item.classList.contains(this.selectedClass) || item.querySelector('input:checked') !== null;
        }) ?? null;
    }

    private isOverflowing(): boolean {
        return this.element.classList.contains(this.overflowingClass);
    }
}
